"use client";

import { Calendar } from "@/public/icons/calendar";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import useToggle from "@/src/hooks/useToggle";
import { DateProvider } from "@/src/providers/LocalizationProvider";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { IconButton, useMediaQuery } from "@mui/material";
import { DesktopDatePicker as DatePicker } from "@mui/x-date-pickers";
import type {
  PickerChangeHandlerContext,
  DateValidationError,
} from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { FieldValues, useController, useFormContext } from "react-hook-form";
import { DatePickerFieldProps } from "../lib/type";
import {
  normalizedDateFormat,
  parseDatePickerValue,
} from "@/src/shared/utils/parseDatePickerValue";
import { getShiftedMinMaxDate } from "../lib/utils";
import { validationRules } from "@/src/shared/utils/validationRules";
import { BreakPointsPx } from "@/src/shared/const";
import { useRef } from "react";
import { parseToKyiv, KYIV_TIMEZONE } from "@/src/shared/utils/dateTime";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";
import {StepAnalytics} from "@/src/shared/models/analytics";

const { DatePicker: DatePickerValidationRules } = validationRules;

export const DatePickerField = <T extends FieldValues>({
  label,
  format = "dd.MM.yyyy",
  returnFormat = normalizedDateFormat,
  defaultValue,
  control,
  name,
  slots = {},
  slotProps = {},
  minDate = "",
  maxDate = "",
  fromNow = false,
  minDaysShift = 0,
  maxDaysShift = 0,
  minYearsShift = 0,
  maxYearsShift = 0,
  shiftStartDateName,
  defaultMinShift,
  defaultMaxShift,
  maxErrorMessage = "",
  minErrorMessage = "",
  errorMessage = "",
  futureDateErrorMessage = "",
  ipnFieldName,
  hintIcon,
  toggleHintArea,
  closeHintArea,
  openHintArea,
  onBlur,
  onFocus,
  onOpen,
  onClose,
  onChange: onChangeController,
  disabled,
  required,
  className,
  readOnly,
  deps,
  analytics,
  ...rest
}: DatePickerFieldProps<T>) => {
  const { callAnalyticEvent } = useAnalytics();
  const isMobile = useMediaQuery(`(max-width:${BreakPointsPx.md})`);
  const isYear = rest.openTo === "year";
  const { getValues } = useFormContext();
  const isAnchorPending = Boolean(shiftStartDateName && !getValues(shiftStartDateName));
  const { min: currentDateMin, max: currentDateMax } = getShiftedMinMaxDate(
    isAnchorPending && defaultMinShift ? defaultMinShift : minDaysShift,
    isAnchorPending && defaultMaxShift ? defaultMaxShift : maxDaysShift,

    minYearsShift,
    maxYearsShift,
    shiftStartDateName ? getValues(shiftStartDateName) || "" : "",
  );

  const min = fromNow ? currentDateMin : minDate;
  const max = fromNow ? currentDateMax : maxDate;
  // The shift-relative bounds are only meaningful once the anchor field has a real
  // value — don't let the "today" placeholder reject a rule-computed value.
  const validationMin = isAnchorPending ? "" : min;
  const validationMax = isAnchorPending ? "" : max;

  const {
    field: { onChange, value = null, name: fieldName, ref },
    fieldState: { error: fieldError },
  } = useController({
    control,
    name,
    rules: DatePickerValidationRules({
      required,
      disabled,
      readOnly,
      min: validationMin,
      max: validationMax,
      minErrorMessage,
      maxErrorMessage,
      errorMessage,
      futureDateErrorMessage,
      deps,
      ipnFieldName,
      isYear,
      getValues
    }),
  });

  const inputRef = useRef(null);

  const {
    isToggled: isDatePickerOpen,
    open: openDatePicker,
    close: closeDatePicker,
    toggle: toggleDatePicker,
  } = useToggle({ initialState: false });

  const callAnalytic = (value: string, analytics?: StepAnalytics) => {
    if (analytics) {
      const { params } = analytics;
      const updatedParams = params?.map(param => {
        if (param.name.toLowerCase().includes("value")) {
          return {
            ...param,
            value,
          };
        }
        return param;
      });

      callAnalyticEvent({...analytics, params: updatedParams});
    }
  }

  const handleDateChange = (
    date: DateTime | null,
    context: PickerChangeHandlerContext<DateValidationError>,
    ref: any,
  ) => {
    // normalize date to start of day to avoid time zone issues
    const normalizedDate = date?.setZone(KYIV_TIMEZONE).startOf('day');
    const formattedDate = normalizedDate?.toFormat(returnFormat);
    if (date) {
      formattedDate && callAnalytic(formattedDate, analytics);
      onChange(formattedDate);
    } else {
      onChange(null);
    }

    if (onChangeController) {
      onChangeController(formattedDate, context, ref);
    }
  };

  const parsedValue = parseDatePickerValue(value || null);

  const formattedMinDate = parseToKyiv(fromNow ? currentDateMin : minDate) ?? undefined;
  const formattedMaxDate = parseToKyiv(fromNow ? currentDateMax : maxDate) ?? undefined;

  return (
    <DateProvider>
      <DatePicker
        {...rest}
        label={label}
        open={isDatePickerOpen}
        onOpen={() => {
          if (isMobile) {
            openDatePicker();
          }
          onOpen?.();
        }}
        onClose={() => {
          closeDatePicker();
          onClose?.();
        }}
        slots={{
          openPickerIcon: Calendar,
          ...slots,
        }}
        slotProps={{
          popper: {
            disablePortal: true,
          },
          dialog: {
            disablePortal: false,
          },
          actionBar: {
            sx: {
              "& .MuiButton-root": {
                color: "var(--blue)",
              },
            },
          },
          yearButton: {
            className: cn(
              "transition-colors rounded-sm",
              "hover:bg-blue hover:text-white focus:bg-blue focus:text-white",
            ),
            color: "var(--blue)",
            sx: {
              "&.Mui-selected": {
                background: "var(--blue)",
              },
            },
          },
          day: {
            disableRipple: true,
            className: cn(
              "transition-colors border-super-light-grey",
              "hover:bg-blue hover:text-white hover:border-super-light-grey focus:bg-blue focus:text-white",
            ),
            sx: {
              "&.Mui-selected": {
                background: "var(--blue)",
              },
            },
          },
          textField: {
            error: !!fieldError,
            helperText: fieldError?.message,
            onBlur,
            // on Mobile focus doesn't work as it is on desktop because of big calendar
            ...(isMobile
              ? {}
              : {
                  onFocus,
                }),
            disabled,
            InputProps: {
              disableUnderline: true,
              disabled,
              readOnly,
              endAdornment: (
                <>
                  <IconButton
                    onClick={toggleDatePicker}
                    className={cn("px-2", { "max-md:px-0": hintIcon })}
                    disabled={disabled || readOnly}
                  >
                    <Calendar />
                  </IconButton>
                  {hintIcon && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleHintArea?.();
                      }}
                      className={cn("px-2", {
                        hidden: !hintIcon,
                        flex: hintIcon,
                        "md:hidden": true,
                      })}
                    >
                      <WebShopIcon name={"questionCircle"} />
                    </IconButton>
                  )}
                </>
              ),
            },
            ...slotProps.textField,
          },
          ...slotProps,
        }}
        name={fieldName}
        onChange={(...args) => handleDateChange(...args, inputRef)}
        onAccept={(...args) => handleDateChange(...args, inputRef)}
        value={parsedValue}
        ref={ref}
        inputRef={inputRef}
        minDate={formattedMinDate}
        maxDate={formattedMaxDate}
        disabled={disabled}
        readOnly={readOnly}
        timezone={KYIV_TIMEZONE}
        className={cn(
          "group",
          { "pointer-events-none": disabled || readOnly },
          className,
        )}
      />
    </DateProvider>
  );
};
